/**
 * ColumnConfigurator - Excel-style column visibility and ordering configuration
 * Two-panel transfer UI: Available Columns ↔ Visible Columns
 * With inline pin controls for left/right pinning
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Input, Tooltip, message } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  UpOutlined,
  DownOutlined,
  SearchOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignBottomOutlined,
  PushpinOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { ColumnConfig, ColumnConfiguratorProps, PinnedColumnsConfig } from './types';
import './ColumnConfigurator.css';

const ColumnConfigurator: React.FC<ColumnConfiguratorProps> = ({
  visible,
  onClose,
  allColumns,
  visibleColumnIds,
  pinnedColumns = { left: [], right: [] },
  onSave,
}) => {
  // Local state for configuration before saving
  const ACTIONS_COL = 'actions_column';
  const [localVisibleIds, setLocalVisibleIds] = useState<string[]>([]);
  const [localPinned, setLocalPinned] = useState<PinnedColumnsConfig>({ left: [], right: [] });
  const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
  const [selectedVisible, setSelectedVisible] = useState<string[]>([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchVisible, setSearchVisible] = useState('');

  // Helper to get all pinned column IDs
  const getAllPinnedIds = (pinned: PinnedColumnsConfig) => [...pinned.left, ...pinned.right];

  // Initialize local state when modal opens
  useEffect(() => {
    if (visible) {
      // Initialize pinned columns state
      setLocalPinned({ left: [...pinnedColumns.left], right: [...pinnedColumns.right] });

      // If no visible column IDs provided, use all columns (including pinned)
      let ids: string[];
      if (visibleColumnIds.length === 0) {
        ids = allColumns.map(col => col.colId);
      } else {
        ids = [...visibleColumnIds];
      }
      if (!ids.includes(ACTIONS_COL)) {
        //ensuring actions column is always visible
        ids.push(ACTIONS_COL);
      }
      setLocalVisibleIds(ids);
      setSelectedAvailable([]);
      setSelectedVisible([]);
      setSearchAvailable('');
      setSearchVisible('');
    }
  }, [visible, visibleColumnIds, allColumns, pinnedColumns]);

  // Get available (hidden) columns
  const availableColumns = allColumns.filter(
    col => !localVisibleIds.includes(col.colId)
  );

  // Get visible columns in order
  const visibleColumnsList = localVisibleIds
    .map(id => allColumns.find(col => col.colId === id))
    .filter((col): col is ColumnConfig => col !== undefined);

  // Filter by search
  const filteredAvailable = availableColumns.filter(col =>
    col.headerName.toLowerCase().includes(searchAvailable.toLowerCase())
  );

  const filteredVisible = visibleColumnsList.filter(col =>
    col.headerName.toLowerCase().includes(searchVisible.toLowerCase())
  );

  // Get pin status of a column
  const getPinStatus = (colId: string): 'left' | 'right' | null => {
    if (localPinned.left.includes(colId)) return 'left';
    if (localPinned.right.includes(colId)) return 'right';
    return null;
  };

  // Pin a column to left or right
  const pinColumn = (colId: string, side: 'left' | 'right', e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalPinned(prev => ({
      left: side === 'left'
        ? [...prev.left.filter(id => id !== colId), colId]
        : prev.left.filter(id => id !== colId),
      right: side === 'right'
        ? [...prev.right.filter(id => id !== colId), colId]
        : prev.right.filter(id => id !== colId),
    }));
  };

  // Unpin a column
  const unpinColumn = (colId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalPinned(prev => ({
      left: prev.left.filter(id => id !== colId),
      right: prev.right.filter(id => id !== colId),
    }));
  };

  // Move selected columns from available to visible
  const moveToVisible = useCallback(() => {
    if (selectedAvailable.length === 0) return;
    setLocalVisibleIds(prev => [...prev, ...selectedAvailable]);
    setSelectedAvailable([]);
  }, [selectedAvailable]);

  // Move selected columns from visible to available
  const moveToAvailable = useCallback(() => {
    if (selectedVisible.length === 0) return;
    if (selectedVisible.includes(ACTIONS_COL)) {//check if trying to hide actions column
      message.error('Actions column cannot be hidden');
      return;
    }
    setLocalVisibleIds(prev => prev.filter(id => !selectedVisible.includes(id)));
    // Also unpin if pinned
    setLocalPinned(prev => ({
      left: prev.left.filter(id => !selectedVisible.includes(id)),
      right: prev.right.filter(id => !selectedVisible.includes(id)),
    }));
    setSelectedVisible([]);
  }, [selectedVisible]);

  // Move selected visible column up
  const moveUp = useCallback(() => {
    if (selectedVisible.length !== 1) return;
    const id = selectedVisible[0];

    setLocalVisibleIds(prev => {
      const index = prev.indexOf(id);
      if (index <= 0) return prev;
      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      return newOrder;
    });
  }, [selectedVisible]);

  // Move selected visible column down
  const moveDown = useCallback(() => {
    if (selectedVisible.length !== 1) return;
    const id = selectedVisible[0];

    setLocalVisibleIds(prev => {
      const index = prev.indexOf(id);
      if (index < 0 || index >= prev.length - 1) return prev;
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  }, [selectedVisible]);

  // Move to top
  const moveToTop = useCallback(() => {
    if (selectedVisible.length !== 1) return;
    const id = selectedVisible[0];

    setLocalVisibleIds(prev => {
      const filtered = prev.filter(colId => colId !== id);
      return [id, ...filtered];
    });
  }, [selectedVisible]);

  // Move to bottom
  const moveToBottom = useCallback(() => {
    if (selectedVisible.length !== 1) return;
    const id = selectedVisible[0];

    setLocalVisibleIds(prev => {
      const filtered = prev.filter(colId => colId !== id);
      return [...filtered, id];
    });
  }, [selectedVisible]);

  // Handle selection in available list
  const handleAvailableClick = (colId: string, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select with Ctrl/Cmd
      setSelectedAvailable(prev =>
        prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]
      );
    } else {
      setSelectedAvailable([colId]);
    }
    setSelectedVisible([]);
  };

  // Handle selection in visible list
  const handleVisibleClick = (colId: string, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedVisible(prev =>
        prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]
      );
    } else {
      setSelectedVisible([colId]);
    }
    setSelectedAvailable([]);
  };

  // Double-click to quickly move
  const handleAvailableDoubleClick = (colId: string) => {
    setLocalVisibleIds(prev => [...prev, colId]);
  };

  const handleVisibleDoubleClick = (colId: string) => {
    if (colId === ACTIONS_COL) {//check if trying to hide actions column
      message.error('Actions column cannot be hidden');
      return;
    }
    setLocalVisibleIds(prev => prev.filter(id => id !== colId));
    // Also unpin if pinned
    setLocalPinned(prev => ({
      left: prev.left.filter(id => id !== colId),
      right: prev.right.filter(id => id !== colId),
    }));
  };

  // Reset to default (show all columns in original order, no pinning)
  const handleReset = () => {
    const defaultVisibleIds = allColumns.map(col => col.colId);
    setLocalVisibleIds(defaultVisibleIds);
    setLocalPinned({ left: [], right: [] });
    setSelectedAvailable([]);
    setSelectedVisible([]);
  };

  // Save and close
  const handleSave = () => {
    onSave(localVisibleIds, localPinned);
    onClose();
  };

  // Cancel - discard changes
  const handleCancel = () => {
    onClose();
  };

  // Render pin controls for a column
  const renderPinControls = (colId: string) => {
    const pinStatus = getPinStatus(colId);

    if (pinStatus) {
      // Already pinned - show indicator and unpin button
      return (
        <div className="pin-controls pinned">
          <span className={`pin-indicator pin-${pinStatus}`}>
            <PushpinOutlined />
            {pinStatus === 'left' ? 'L' : 'R'}
          </span>
          <Tooltip title="Unpin">
            <button
              className="pin-btn unpin-btn"
              onClick={(e) => unpinColumn(colId, e)}
            >
              <CloseOutlined />
            </button>
          </Tooltip>
        </div>
      );
    }

    // Not pinned - show pin left/right buttons on hover
    return (
      <div className="pin-controls">
        <Tooltip title="Pin to left">
          <button
            className="pin-btn"
            onClick={(e) => pinColumn(colId, 'left', e)}
          >
            <PushpinOutlined style={{ transform: 'rotate(-45deg)' }} />
          </button>
        </Tooltip>
        <Tooltip title="Pin to right">
          <button
            className="pin-btn"
            onClick={(e) => pinColumn(colId, 'right', e)}
          >
            <PushpinOutlined style={{ transform: 'rotate(45deg)' }} />
          </button>
        </Tooltip>
      </div>
    );
  };

  return (
    <Modal
      title="Configure Table Columns"
      open={visible}
      onCancel={handleCancel}
      width={700}
      centered
      footer={[
        <Button key="reset" onClick={handleReset}>
          Reset to Default
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save
        </Button>,
      ]}
      className="column-configurator-modal"
    >
      <div className="column-configurator">
        <div className="column-panels">
          {/* Available Columns Panel */}
          <div className="column-panel">
            <div className="panel-header">Available Columns</div>
            <Input
              placeholder="Search columns..."
              prefix={<SearchOutlined />}
              value={searchAvailable}
              onChange={e => setSearchAvailable(e.target.value)}
              className="panel-search"
              allowClear
            />
            <div className="column-list">
              {filteredAvailable.length === 0 ? (
                <div className="empty-message">No hidden columns</div>
              ) : (
                filteredAvailable.map(col => (
                  <div
                    key={col.colId}
                    className={`column-item ${selectedAvailable.includes(col.colId) ? 'selected' : ''}`}
                    onClick={e => handleAvailableClick(col.colId, e)}
                    onDoubleClick={() => handleAvailableDoubleClick(col.colId)}
                  >
                    {col.headerName}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Transfer Buttons */}
          <div className="transfer-buttons">
            <Button
              icon={<RightOutlined />}
              onClick={moveToVisible}
              disabled={selectedAvailable.length === 0}
              title="Show selected columns"
            />
            <Button
              icon={<LeftOutlined />}
              onClick={moveToAvailable}
              disabled={selectedVisible.length === 0}
              title="Hide selected columns"
            />
          </div>

          {/* Visible Columns Panel */}
          <div className="column-panel">
            <div className="panel-header">
              Visible Columns <span className="column-count">({visibleColumnsList.length})</span>
            </div>
            <Input
              placeholder="Search columns..."
              prefix={<SearchOutlined />}
              value={searchVisible}
              onChange={e => setSearchVisible(e.target.value)}
              className="panel-search"
              allowClear
            />
            <div className="column-list">
              {filteredVisible.map((col, index) => {
                const pinStatus = getPinStatus(col.colId);
                const isActionsColumn = col.colId === ACTIONS_COL;
                return (
                  <div
                    key={col.colId}
                    className={`column-item ${selectedVisible.includes(col.colId) ? 'selected' : ''} ${
                      pinStatus ? `pinned-${pinStatus}` : ''
                    } ${isActionsColumn ? 'column-item-locked' : ''}`}
                    onClick={e => handleVisibleClick(col.colId, e)}
                    onDoubleClick={() => handleVisibleDoubleClick(col.colId)}
                    title={isActionsColumn ? 'Actions column cannot be hidden' : ''}
                  >
                    <span className="column-index">{index + 1}.</span>
                    {isActionsColumn && <span title="Cannot be hidden">🔒</span>}
                    <span className="column-name">{col.headerName}</span>
                    {renderPinControls(col.colId)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reorder Buttons */}
          <div className="reorder-buttons">
            <Button
              icon={<VerticalAlignTopOutlined />}
              onClick={moveToTop}
              disabled={selectedVisible.length !== 1}
              title="Move to top"
            />
            <Button
              icon={<UpOutlined />}
              onClick={moveUp}
              disabled={selectedVisible.length !== 1}
              title="Move up"
            />
            <Button
              icon={<DownOutlined />}
              onClick={moveDown}
              disabled={selectedVisible.length !== 1}
              title="Move down"
            />
            <Button
              icon={<VerticalAlignBottomOutlined />}
              onClick={moveToBottom}
              disabled={selectedVisible.length !== 1}
              title="Move to bottom"
            />
          </div>
        </div>

        <div className="configurator-hint">
          Tip: Double-click to quickly move columns. Hover over visible columns to pin left/right.
        </div>
      </div>
    </Modal>
  );
};

export default ColumnConfigurator;
